import React from 'react';
import {
  DataSource,
  DATA_SOURCE_COLORS,
  CONFIDENCE_LEVELS,
} from '../../types/gaushalaMap.types';

/**
 * Data Source Indicator Component
 *
 * Shows whether location data comes from:
 * - database (verified)
 * - geocoded_from_pincode (high confidence)
 * - geocoded_from_address (medium confidence)
 * - geocoded_from_name (low confidence)
 * - fallback (very low confidence)
 */

interface DataSourceIndicatorProps {
  dataSource: DataSource;
  confidence: 'high' | 'medium' | 'low';
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const getDataSourceLabel = (dataSource: DataSource): string => {
  const labels: Record<DataSource, string> = {
    database: 'Database',
    geocoded_from_pincode: 'From Pincode',
    geocoded_from_address: 'From Address',
    geocoded_from_name: 'From Name',
    fallback: 'Approximate',
  };
  return labels[dataSource];
};

const getDataSourceDescription = (dataSource: DataSource): string => {
  const descriptions: Record<DataSource, string> = {
    database: 'Verified location from database',
    geocoded_from_pincode:
      'Location detected from pincode',
    geocoded_from_address:
      'Location detected from address',
    geocoded_from_name: 'Location detected from gaushala name',
    fallback: 'Location is approximate',
  };
  return descriptions[dataSource];
};

const DataSourceIndicator: React.FC<DataSourceIndicatorProps> = ({
  dataSource,
  confidence,
  showLabel = false,
  size = 'md',
}) => {
  const color =
    DATA_SOURCE_COLORS[dataSource as keyof typeof DATA_SOURCE_COLORS] ||
    DATA_SOURCE_COLORS.fallback;

  const confidenceInfo =
    CONFIDENCE_LEVELS[confidence as keyof typeof CONFIDENCE_LEVELS];

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-xs px-2 py-1',
    lg: 'text-sm px-3 py-1.5',
  };

  return (
    <div className="flex items-center gap-2">
      {/* Icon Indicator */}
      <div
        className={`flex items-center justify-center rounded-full w-5 h-5 flex-shrink-0`}
        style={{ backgroundColor: color }}
        title={getDataSourceDescription(dataSource)}
      >
        <span className="text-white text-xs font-bold">
          {dataSource === 'database' ? '✓' : '🌐'}
        </span>
      </div>

      {/* Text Label */}
      {showLabel && (
        <div className="flex-1 min-w-0">
          <span
            className={`inline-block ${sizeClasses[size]} font-semibold rounded text-white`}
            style={{ backgroundColor: color }}
            title={getDataSourceDescription(dataSource)}
          >
            {getDataSourceLabel(dataSource)}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Data Source Badge Component
 * Used for displaying multiple sources in a list/table
 */
interface DataSourceBadgeProps {
  dataSource: DataSource;
  confidence: 'high' | 'medium' | 'low';
}

export const DataSourceBadge: React.FC<DataSourceBadgeProps> = ({
  dataSource,
  confidence,
}) => {
  const color =
    DATA_SOURCE_COLORS[dataSource as keyof typeof DATA_SOURCE_COLORS] ||
    DATA_SOURCE_COLORS.fallback;

  const confidenceInfo =
    CONFIDENCE_LEVELS[confidence as keyof typeof CONFIDENCE_LEVELS];

  return (
    <div className="flex items-center gap-1">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: color }}
      ></div>
      <span className="text-xs font-medium text-gray-700">
        {getDataSourceLabel(dataSource)}
      </span>
      <span className="text-xs text-gray-500">({confidenceInfo.label})</span>
    </div>
  );
};

export default DataSourceIndicator;
